import { useMemo } from 'react';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { OAuthConsentFeatureGate } from '../../../FeatureGates/FeatureGates';
import { useInfo } from '../../../Flow/ConsentContextProvider';
import type { ConsentToken } from '#definitions/context';
import { getOrderedPolicySources } from './policyState';
import {
  classifyScopesWithOverride,
  hasAtlassianScopes,
  hasTrelloScopes,
} from './trelloScopes';

/**
 * The MCP consent screen being rendered. Agent and Forge are named for their
 * consent target, which takes precedence over the selectors shown: Agent MCP
 * renders none, and Forge MCP renders the same ones a standard flow would (read
 * those off the visibility flags). The rest are named for the selectors on
 * screen.
 */
export const MCP_FLOW = {
  /** Rovo Agent consent: site locked server-side, no selectors. */
  agent: 'agent',
  /** Forge app consent. */
  forge: 'forge',
  site: 'site',
  siteBitbucket: 'site+bitbucket',
  /** Atlassian and Trello scopes together; needs the mixed-workspace gate. */
  siteTrello: 'site+trello',
  siteTrelloBitbucket: 'site+trello+bitbucket',
  /** Trello scopes only, with Trello product access. */
  trello: 'trello',
  /** Trello scopes only, but the user has no Trello product access. */
  trelloNoAccess: 'trelloNoAccess',
  /** Only global scopes, with Bitbucket workspaces to grant. */
  bitbucket: 'bitbucket',
  /** Only global scopes and no Bitbucket workspaces: no selector at all. */
  none: 'none',
} as const;

export type McpFlow = (typeof MCP_FLOW)[keyof typeof MCP_FLOW];

type SelectorVisibilityArgs = {
  scopeClassification: { hasTrello: boolean; hasAtlassian: boolean };
  isBitbucketWorkspaceAccessEnabled: boolean;
  trelloRequestedButNoAccess: boolean;
  agentMcpResource?: string;
  isForgeMcp?: boolean;
};

/**
 * Which flow MCP renders and which selectors it shows, decided once so the
 * selectors, the org match and McpFlowParameters cannot drift apart. Agent MCP
 * renders no selector: its site is locked server-side. Bitbucket is the one
 * source whose participation is decided by /consent/info rather than by the
 * scopes, so it is also hidden whenever 'bitbucket' is outside
 * getOrderedPolicySources — see BitbucketWorkspaceSelector.
 */
export function getSelectorVisibility(args: SelectorVisibilityArgs) {
  const { scopeClassification, isBitbucketWorkspaceAccessEnabled } = args;
  const showSiteSelector =
    !args.agentMcpResource && scopeClassification.hasAtlassian;
  const showBitbucketSelector =
    !args.agentMcpResource &&
    isBitbucketWorkspaceAccessEnabled &&
    getOrderedPolicySources(scopeClassification).includes('bitbucket');
  return {
    mcpFlow: getMcpFlow(args, showBitbucketSelector),
    showSiteSelector,
    showBitbucketSelector,
  };
}

function getMcpFlow(
  {
    scopeClassification: { hasTrello, hasAtlassian },
    trelloRequestedButNoAccess,
    agentMcpResource,
    isForgeMcp,
  }: SelectorVisibilityArgs,
  showBitbucketSelector: boolean,
): McpFlow {
  if (agentMcpResource) return MCP_FLOW.agent;
  if (isForgeMcp) return MCP_FLOW.forge;
  if (hasAtlassian) {
    if (hasTrello) {
      return showBitbucketSelector
        ? MCP_FLOW.siteTrelloBitbucket
        : MCP_FLOW.siteTrello;
    }
    return showBitbucketSelector ? MCP_FLOW.siteBitbucket : MCP_FLOW.site;
  }
  if (hasTrello) return MCP_FLOW.trello;
  if (trelloRequestedButNoAccess) return MCP_FLOW.trelloNoAccess;
  return showBitbucketSelector ? MCP_FLOW.bitbucket : MCP_FLOW.none;
}

/** The flows whose site and Bitbucket selectors go multi-select under the gate. */
const MULTI_SELECT_FLOWS: ReadonlySet<McpFlow> = new Set([
  MCP_FLOW.site,
  MCP_FLOW.siteBitbucket,
  MCP_FLOW.bitbucket,
]);

/**
 * Whether the site and Bitbucket selectors allow more than one selection.
 * Forge, Agent and every Trello flow stay single-select whatever the gate says.
 */
export function isSiteBitbucketMultiSelectFlow(
  mcpFlow: McpFlow,
  isGateEnabled: boolean,
): boolean {
  return isGateEnabled && MULTI_SELECT_FLOWS.has(mcpFlow);
}

/**
 * What the MCP consent screen is for this token and /consent/info: which
 * product lines it covers, which selectors it renders, whether those allow
 * several selections, and which policy sources speak for it.
 */
export function useMcpFlow({
  token,
  isBitbucketWorkspaceAccessEnabled,
  agentMcpResource,
  isForgeMcp,
}: {
  token: ConsentToken;
  isBitbucketWorkspaceAccessEnabled: boolean;
  agentMcpResource?: string;
  isForgeMcp: boolean;
}) {
  const info = useInfo();
  const trelloMixedWorkspaceEnabled = useFeatureGate(
    OAuthConsentFeatureGate.TRELLO_MCP_MIXED_WORKSPACE,
  );
  const siteBitbucketMultiSelectGateEnabled = useFeatureGate(
    OAuthConsentFeatureGate.MCP_MULTI_SITE_BITBUCKET_SELECTION,
  );

  const twoStepConsentGateEnabled = useFeatureGate(
    OAuthConsentFeatureGate.MCP_TWO_STEP_CONSENT,
  );

  const scopeClassification = useMemo(
    () =>
      classifyScopesWithOverride(
        token.scopes ?? [],
        info.hasTrelloProductAccess,
        trelloMixedWorkspaceEnabled,
      ),
    [token.scopes, info.hasTrelloProductAccess, trelloMixedWorkspaceEnabled],
  );
  const trelloRequestedButNoAccess = useMemo(() => {
    const scopes = token.scopes ?? [];
    return (
      hasTrelloScopes(scopes) &&
      !hasAtlassianScopes(scopes) &&
      info.hasTrelloProductAccess !== true
    );
  }, [token.scopes, info.hasTrelloProductAccess]);
  const orderedPolicySources = useMemo(
    () => getOrderedPolicySources(scopeClassification),
    [scopeClassification],
  );

  const { mcpFlow, showSiteSelector, showBitbucketSelector } =
    getSelectorVisibility({
      scopeClassification,
      isBitbucketWorkspaceAccessEnabled,
      trelloRequestedButNoAccess,
      agentMcpResource,
      isForgeMcp,
    });

  const isSiteBitbucketMultiSelectEnabled = isSiteBitbucketMultiSelectFlow(
    mcpFlow,
    siteBitbucketMultiSelectGateEnabled,
  );

  return {
    scopeClassification,
    trelloRequestedButNoAccess,
    orderedPolicySources,
    showSiteSelector,
    showBitbucketSelector,
    isSiteBitbucketMultiSelectEnabled,
    isTwoStepConsentEnabled:
      isSiteBitbucketMultiSelectEnabled && twoStepConsentGateEnabled,
  };
}
